import { defineMessages, FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { useCallback, useState } from "react";

import { Heading, Paragraph } from "common/core/typography";
import { Card, CardSection } from "common/core/card";
import LockSuccessIcon from "assets/images/mfa_success.svg";
import WorkflowModal from "common/modals/workflow_modal";
import Button from "common/core/button";
import { pushNotification } from "common/core/notification_center/actions";
import { captureException } from "util/exception";
import { StyledTextInput } from "common/core/form/text";
import { useForm } from "common/core/form";
import { FieldErrorMessage, isAriaInvalid } from "common/core/form/error";
import { useActiveOrganization } from "common/account/active_organization";
import LoadingIndicator from "common/core/loading_indicator";
import { useMutation, useQuery } from "util/graphql";

import Styles from "./index.module.scss";
import CommonStyles from "../../profile/overview/index.module.scss";
import CreateOauthApplicationMutation from "./create_oauth_application_mutation.graphql";
import GenerateOauthApplicationSecretMutation from "./generate_oauth_application_secret_mutation.graphql";
import GetOAuthApplicationsQuery, {
  type GetOAuthApplications_organization_Organization_oauthApplications as OAuthApplication,
} from "./get_oauth_applications.graphql";

const MESSAGES = defineMessages({
  createMessage: {
    id: "87daacfd-b2f1-4e32-81b9-dfc361125706",
    defaultMessage: "Create client application",
  },
  createModalHeading: {
    id: "0c0c27c3-55fe-4a76-9351-f88ae74db86d",
    defaultMessage: "Create new client application",
  },
  cancel: {
    id: "489ee0cf-b972-4916-ab6f-a54b5682984a",
    defaultMessage: "Cancel",
  },
  successMessage: {
    id: "a78839e7-a14e-4b21-8baf-5d650307aa4a",
    defaultMessage: "Client application created",
  },
  failureMessage: {
    id: "637be10b-894b-44a8-a74a-04535daac9be",
    defaultMessage: "Client application creation failed",
  },
  forbiddenMessage: {
    id: "54467ce2-c3f2-485d-beb3-680c8cc8c6d0",
    defaultMessage: "You are not permitted to create a new application",
  },
  nameFieldLabel: {
    id: "a41fa48d-46d5-46b3-b483-7e46cc747f92",
    defaultMessage: "Display name",
  },
  nameRequiredMessage: {
    id: "4fe4c64e-efb1-4d21-bd68-ae07fd5a69dc",
    defaultMessage: "Client application name is required",
  },
  nameFieldMaxLength: {
    id: "70269f4b-9fe1-4ca2-adb1-57a10711e3a3",
    defaultMessage: "Client application name is too long",
  },
  applicationsHeading: {
    id: "577d5b6e-a9c9-496e-aedc-cd6cfb692e05",
    defaultMessage: "Client applications",
  },
  noApplicationsFound: {
    id: "3c6ae547-1c2c-45fe-9828-e451a45ea3a6",
    defaultMessage: "No OAuth applications found",
  },
  applicationDetails: {
    id: "57b932e1-c300-4c23-9bfb-4095ac6cf349",
    defaultMessage: "Application details",
  },
  applicationId: {
    id: "0cd33d60-7f54-4a0f-ae0c-01cdbd5f8a81",
    defaultMessage: "Application ID",
  },
  createdAt: {
    id: "21bac3e8-efb0-4c5e-b02c-d74502224451",
    defaultMessage: "Created At",
  },
  addAnotherApplication: {
    id: "b380b958-18b3-45d6-bc3e-329499f35d47",
    defaultMessage: "Add another client application",
  },
  deleteApplication: {
    id: "f24227a2-7c7e-4b85-aaf4-e25ce159d3ef",
    defaultMessage: "Delete",
  },
  viewApplication: {
    id: "290c8fe5-85d4-4062-80f4-f60b2c0a5a6e",
    defaultMessage: "View client application",
  },
});

function InitialCreationCard({ openCreateModal }: { openCreateModal: () => void }) {
  const intl = useIntl();

  return (
    <Card fullWidth>
      <CardSection>
        <div className={Styles.contentWrapper}>
          <div>
            <img src={LockSuccessIcon} aria-hidden="true" alt="" width="120" />
            <Heading level="h3" textStyle="headingFour" className={CommonStyles.heading}>
              <FormattedMessage
                id="8c819680-e1e6-4f98-b197-5e455916261d"
                defaultMessage="Create your first client application"
              />
            </Heading>
            <div className={Styles.textContainer}>
              <Paragraph className={CommonStyles.copy}>
                <FormattedMessage
                  id="188ca57e-64a7-4490-8702-1d21cc716150"
                  defaultMessage="Add a client application with a unique display name. We will generate an app ID and secret(s) for your use."
                />
              </Paragraph>
            </div>
          </div>
          <Button onClick={openCreateModal} buttonColor="action" variant="primary">
            {intl.formatMessage(MESSAGES.createMessage)}
          </Button>
        </div>
      </CardSection>
    </Card>
  );
}

function ApplicationsList({
  applications,
  intl,
}: {
  applications: OAuthApplication[];
  intl: IntlShape;
}) {
  return (
    <div>
      {applications.map((app) => (
        <Card key={app.id} className={Styles.applicationCard} fullWidth>
          <CardSection>
            <div className={Styles.applicationCardContent}>
              <div>
                <Heading level="h4" textStyle="headingFive">
                  {app.name}
                </Heading>
                <div className={Styles.applicationDetails}>
                  <Paragraph className={Styles.createdDate}>
                    Created on{" "}
                    {new Date(app.createdAt).toLocaleDateString("en-US", {
                      month: "numeric",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    at{" "}
                    {new Date(app.createdAt).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}{" "}
                    {
                      new Date(app.createdAt)
                        .toLocaleTimeString("en-US", { timeZoneName: "short" })
                        .split(" ")[2]
                    }
                  </Paragraph>
                  <Paragraph className={Styles.appId}>
                    App ID: <span className={Styles.appIdValue}>{app.id}</span>
                  </Paragraph>
                </div>
              </div>
              <div className={Styles.applicationCardActions}>
                <Button buttonColor="danger" variant="tertiary">
                  {intl.formatMessage(MESSAGES.deleteApplication)}
                </Button>
                <Button buttonColor="action" variant="secondary">
                  {intl.formatMessage(MESSAGES.viewApplication)}
                </Button>
              </div>
            </div>
          </CardSection>
        </Card>
      ))}
    </div>
  );
}

export function OAuthSettingsContent() {
  const intl = useIntl();
  const [currentModal, setCurrentModal] = useState<"create" | "delete" | null>(null);

  type FormValues = {
    displayName: string;
  };

  const form = useForm<FormValues>({
    defaultValues: {
      displayName: "",
    },
  });

  const {
    formState: { errors },
  } = form;

  const closeModal = useCallback(() => {
    setCurrentModal(null);
    form.reset();
    form.clearErrors();
  }, [form]);

  const createOauthApplicationMutateFn = useMutation(CreateOauthApplicationMutation);
  const generateOauthApplicationSecretMutateFn = useMutation(
    GenerateOauthApplicationSecretMutation,
  );
  const [organizationId] = useActiveOrganization();

  const { data, loading, refetch } = useQuery(GetOAuthApplicationsQuery, {
    variables: { organizationId: organizationId as string },
  });

  const organization = data?.organization;
  const applications =
    organization && "oauthApplications" in organization ? organization.oauthApplications : [];

  const onSubmit = (formValues: FormValues) => {
    return createOauthApplicationMutateFn({
      variables: {
        input: { organizationId: organizationId as string, name: formValues.displayName },
      },
    })
      .then((response) => {
        const newApplicationId = response.data!.createOAuthApplication!.oauthApplication.id;

        return generateOauthApplicationSecretMutateFn({
          variables: {
            input: { id: newApplicationId },
          },
        }).then(() => {
          pushNotification({
            subtype: "SUCCESS",
            message: intl.formatMessage(MESSAGES.successMessage),
          });
          closeModal();
          // TODO PLAT-5832 should navigate to the newly created application details page instead of refetching
          refetch();
        });
      })
      .catch((error: Error) => {
        captureException(error);
        pushNotification({
          subtype: "ERROR",
          message:
            error.message === "forbidden"
              ? intl.formatMessage(MESSAGES.forbiddenMessage)
              : intl.formatMessage(MESSAGES.failureMessage),
        });
      });
  };

  return (
    <>
      {loading ? (
        <LoadingIndicator />
      ) : (
        <div>
          <div className={Styles.applicationsHeader}>
            <Heading level="h3" textStyle="headingFour">
              {intl.formatMessage(MESSAGES.applicationsHeading)}
            </Heading>
            {applications.length > 0 && (
              <Button
                onClick={() => setCurrentModal("create")}
                buttonColor="action"
                variant="primary"
              >
                {intl.formatMessage(MESSAGES.addAnotherApplication)}
              </Button>
            )}
          </div>

          {applications.length === 0 ? (
            <InitialCreationCard openCreateModal={() => setCurrentModal("create")} />
          ) : (
            <ApplicationsList applications={applications} intl={intl} />
          )}
        </div>
      )}

      {currentModal === "create" && (
        <WorkflowModal
          autoFocus
          title={intl.formatMessage(MESSAGES.createModalHeading)}
          buttons={[
            <Button
              key="cancel"
              onClick={() => closeModal()}
              buttonColor="dark"
              variant="tertiary"
              disabled={form.formState.isSubmitting}
            >
              {intl.formatMessage(MESSAGES.cancel)}
            </Button>,
            <Button
              key="submit"
              onClick={form.handleSubmit(onSubmit)}
              type="submit"
              buttonColor="action"
              variant="primary"
              isLoading={form.formState.isSubmitting}
            >
              {intl.formatMessage(MESSAGES.createMessage)}
            </Button>,
          ]}
        >
          <div>
            <StyledTextInput
              data-automation-id="display-name-input"
              aria-invalid={isAriaInvalid(errors.displayName)}
              disabled={form.formState.isSubmitting}
              label={intl.formatMessage(MESSAGES.nameFieldLabel)}
              displayRequiredAsterisk
              {...form.register("displayName", {
                required: true,
                maxLength: {
                  value: 256,
                  message: intl.formatMessage(MESSAGES.nameFieldMaxLength),
                },
              })}
            />
            {errors.displayName && (
              <div>
                <FieldErrorMessage
                  inputName="displayName"
                  message={
                    errors.displayName.message || intl.formatMessage(MESSAGES.nameRequiredMessage)
                  }
                />
              </div>
            )}
          </div>
        </WorkflowModal>
      )}
    </>
  );
}
